import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJWTAuth } from '../context/JWTAuthContext';
import travelApiService from '../utils/travelApiService';
import './TravelPlanPage.css';

const TravelPlanPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useJWTAuth();
  const [travelPlans, setTravelPlans] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false); // 동기화 중 상태
  const [newPlan, setNewPlan] = useState({
    title: '',
    destination: '',
    startDate: '',
    endDate: '',
    budget: '',
    description: '',
    participants: [] // 참여 인원 배열
  });

  // 사용 가능한 아이콘 목록
  const availableIcons = [
    '👤', '👥', '👨', '👩', '🧑', '👶', '👧', '👦', '🧒',
    '👨‍🦰', '👩‍🦰', '👨‍🦱', '👩‍🦱', '👨‍🦲', '👩‍🦲', '👨‍🦳', '👩‍🦳',
    '🧔', '🧔‍♂️', '🧔‍♀️', '👱', '👱‍♂️', '👱‍♀️'
  ];

  // 새 참여자 추가
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    name: '',
    contact: '', // email 또는 전화번호
    icon: '👤' // 기본 아이콘
  });

  // 로컬 스토리지에서 여행 계획 목록 불러오기
  useEffect(() => {
    const savedPlans = localStorage.getItem('travelPlans');
    if (savedPlans) {
      setTravelPlans(JSON.parse(savedPlans));
    }
  }, []);

  // 여행 계획 목록을 로컬 스토리지에 저장
  const savePlansToStorage = (plans) => {
    localStorage.setItem('travelPlans', JSON.stringify(plans));
  };

  // 참여자 추가
  const handleAddParticipant = () => {
    if (!newParticipant.name.trim()) {
      alert('참여자 이름은 필수입니다.');
      return;
    }

    const participantId = Date.now().toString();
    const participant = {
      id: participantId,
      name: newParticipant.name.trim(),
      contact: newParticipant.contact.trim(),
      icon: newParticipant.icon || '👤'
    };

    setNewPlan(prev => ({
      ...prev,
      participants: [...prev.participants, participant]
    }));

    // 폼 초기화
    setNewParticipant({ name: '', contact: '', icon: '👤' });
    setShowAddParticipant(false);
  };

  // 참여자 삭제
  const handleRemoveParticipant = (participantId) => {
    setNewPlan(prev => ({
      ...prev,
      participants: prev.participants.filter(p => p.id !== participantId)
    }));
  };

  // 새 여행 계획 모달 열기 (기본 참여자 '나' 추가)
  const handleOpenCreateModal = () => {
    // 폼 초기화 및 기본 참여자 '나' 추가
    setNewPlan({
      title: '',
      destination: '',
      startDate: '',
      endDate: '',
      budget: '',
      description: '',
      participants: [{
        id: `me-${Date.now()}`,
        name: '나',
        contact: '',
        icon: '👤'
      }]
    });
    setShowCreateModal(true);
  };

  // 새 여행 계획 생성
  const handleCreatePlan = async () => {
    // 필수: 목적지, 시작 날짜, 종료 날짜
    if (!newPlan.destination || !newPlan.startDate || !newPlan.endDate) {
      alert('목적지와 날짜를 모두 입력해주세요.');
      return;
    }

    const startDate = new Date(newPlan.startDate);
    const endDate = new Date(newPlan.endDate);

    if (startDate > endDate) {
      alert('종료 날짜는 시작 날짜보다 늦어야 합니다.');
      return;
    }

    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    // 로컬 임시 ID 생성 (타임스탬프 기반)
    const tempLocalId = `local-${Date.now()}`;

    // 여행 제목이 없으면 목적지를 제목으로 사용
    const planTitle = newPlan.title.trim() || newPlan.destination;

    const newTravelPlan = {
      id: tempLocalId, // 로컬 ID (서버 저장 실패 시 사용)
      ...newPlan,
      title: planTitle,
      duration: daysDiff,
      createdAt: new Date().toISOString(),
      days: Array.from({ length: daysDiff }, (_, index) => ({
        date: new Date(startDate.getTime() + index * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        activities: []
      }))
    };

    // 1. 먼저 로컬에 저장
    const updatedPlans = [...travelPlans, newTravelPlan];
    setTravelPlans(updatedPlans);
    savePlansToStorage(updatedPlans);

    // 2. 로그인 상태이면 서버에도 저장 시도
    if (isAuthenticated && user) {
      try {
        console.log('🔄 [TravelPlanPage] 서버에 여행 계획 저장 중...');
        setIsSyncing(true);

        // 서버에 저장할 데이터 (참여자 포함)
        const planDataForServer = {
          title: planTitle,
          destination: newPlan.destination,
          startDate: newPlan.startDate,
          endDate: newPlan.endDate,
          budget: parseInt(newPlan.budget) || 0,
          description: newPlan.description || '',
        };

        const response = await travelApiService.createTravelPlan(planDataForServer);

        if (response.success && response.data && response.data.id) {
          const serverId = response.data.id;
          console.log('✅ [TravelPlanPage] 서버 저장 성공, serverId:', serverId);

          // 3. 로컬 ID를 서버 ID로 교체
          const planWithRealId = {
            ...newTravelPlan,
            id: serverId, // 로컬 임시 ID를 서버 ID로 교체
          };

          // 4. 참여자들도 서버에 저장하고 ID 교체
          if (newPlan.participants && newPlan.participants.length > 0) {
            const participantsWithRealId = [];
            for (const participant of newPlan.participants) {
              try {
                const participantResponse = await travelApiService.createParticipant(serverId, {
                  name: participant.name,
                  contact: participant.contact || '',
                  icon: participant.icon || '👤',
                });

                if (participantResponse.success && participantResponse.data) {
                  participantsWithRealId.push({
                    ...participant,
                    id: participantResponse.data.id, // 로컬 ID를 서버 ID로 교체
                  });
                  console.log('✅ [TravelPlanPage] 참여자 저장 성공:', participant.name, 'id:', participantResponse.data.id);
                } else {
                  // 실패해도 로컬 데이터는 유지
                  participantsWithRealId.push(participant);
                }
              } catch (error) {
                console.error('❌ [TravelPlanPage] 참여자 저장 실패:', participant.name, error);
                participantsWithRealId.push(participant);
              }
            }
            planWithRealId.participants = participantsWithRealId;
          }

          // 5. localStorage 업데이트 (임시 ID를 서버 ID로 교체)
          const syncedPlans = updatedPlans.map(plan =>
            plan.id === tempLocalId ? planWithRealId : plan
          );
          setTravelPlans(syncedPlans);
          savePlansToStorage(syncedPlans);

          console.log('✅ [TravelPlanPage] 로컬 ID를 서버 ID로 교체 완료:', tempLocalId, '→', serverId);
        } else {
          throw new Error('서버 응답에 ID가 없습니다.');
        }
      } catch (error) {
        console.error('❌ [TravelPlanPage] 서버 저장 실패:', error);
        // 서버 저장 실패해도 로컬에는 저장되어 있음 (오프라인 지원)
        console.log('⚠️ [TravelPlanPage] 로컬에만 저장되었습니다. 나중에 서버 동기화 가능합니다.');
      } finally {
        setIsSyncing(false);
      }
    } else {
      console.log('⚠️ [TravelPlanPage] 비로그인 상태 - 로컬에만 저장됩니다.');
    }

    // 폼 초기화 및 모달 닫기
    setNewPlan({
      title: '',
      destination: '',
      startDate: '',
      endDate: '',
      budget: '',
      description: '',
      participants: []
    });
    setShowCreateModal(false);
  };

  // 여행 계획 삭제
  const handleDeletePlan = async (planId) => {
    if (!window.confirm('정말로 이 여행 계획을 삭제하시겠습니까?')) {
      return;
    }

    // 로컬 ID인지 서버 ID인지 확인
    const isLocalId = typeof planId === 'string' && planId.startsWith('local-');

    // 1. 로컬스토리지에서 삭제
    const updatedPlans = travelPlans.filter(plan => plan.id !== planId);
    setTravelPlans(updatedPlans);
    savePlansToStorage(updatedPlans);

    // 2. 서버 ID이고 로그인 상태이면 서버에서도 삭제
    if (!isLocalId && isAuthenticated) {
      try {
        console.log('🗑️ [TravelPlanPage] 서버에서 여행 계획 삭제 중... ID:', planId);
        const response = await travelApiService.deleteTravelPlan(planId);

        if (response.success) {
          console.log('✅ [TravelPlanPage] 서버 삭제 완료');
        } else {
          throw new Error(response.message || '서버 삭제 실패');
        }
      } catch (error) {
        console.error('❌ [TravelPlanPage] 서버 삭제 실패:', error);
        alert(`로컬에서는 삭제되었지만 서버 삭제 실패: ${error.message}\n다시 시도해주세요.`);
        // 로컬에서는 이미 삭제됨
      }
    } else {
      console.log('⚠️ [TravelPlanPage] 로컬에만 저장된 계획 삭제 (서버 요청 없음)');
    }
  };

  // 서버에서 불러오기
  const handleLoadFromServer = async () => {
    if (!isAuthenticated) {
      alert('로그인이 필요합니다.');
      return;
    }

    if (travelPlans.length > 0) {
      const confirm = window.confirm(
        '서버에서 불러오면 현재 로컬 데이터가 덮어쓰여집니다.\n계속하시겠습니까?'
      );
      if (!confirm) return;
    }

    setIsSyncing(true);
    try {
      console.log('🔄 서버에서 여행 계획 불러오는 중...');
      const response = await travelApiService.getTravelPlans();

      if (response.success && response.data) {
        const serverPlans = response.data;
        console.log('✅ 서버에서 불러온 여행 계획:', serverPlans.length, '개');

        // 서버 데이터를 프론트엔드 형식으로 변환
        const convertedPlans = serverPlans.map((serverPlan) => {
          // ISO 8601 날짜를 "YYYY-MM-DD" 형식으로 변환
          const convertDate = (isoDate) => {
            if (!isoDate) return '';
            return isoDate.split('T')[0]; // "2026-01-01T00:00:00Z" → "2026-01-01"
          };

          return {
            id: serverPlan.id,
            title: serverPlan.title,
            destination: serverPlan.destination,
            startDate: convertDate(serverPlan.start_date), // snake_case → camelCase
            endDate: convertDate(serverPlan.end_date),
            duration: serverPlan.duration,
            budget: serverPlan.budget,
            description: serverPlan.description,
            createdAt: serverPlan.created_at,
            participants: serverPlan.participants || [],
            days: serverPlan.days || [],
            transportations: serverPlan.transportations || [],
            accommodations: serverPlan.accommodations || [],
            checklist: serverPlan.checklist || [],
            is_owner: serverPlan.is_owner,
            permission: serverPlan.permission,
          };
        });

        console.log('✅ 서버 데이터를 프론트엔드 형식으로 변환 완료');

        setTravelPlans(convertedPlans);
        savePlansToStorage(convertedPlans);

        alert(`서버에서 ${convertedPlans.length}개의 여행 계획을 불러왔습니다.`);
      } else {
        throw new Error(response.message || '데이터를 불러올 수 없습니다.');
      }
    } catch (error) {
      console.error('❌ 서버 불러오기 실패:', error);
      alert(`서버에서 불러오기 실패: ${error.message}`);
    } finally {
      setIsSyncing(false);
    }
  };

  // 여행 계획 상세보기로 이동
  const handleViewPlan = (planId) => {
    navigate(`/travel/${planId}`);
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 예산 포맷팅
  const formatBudget = (budget) => {
    if (!budget) return '예산 미정';
    return `${parseInt(budget).toLocaleString()}원`;
  };

  return (
    <div className="travel-plan-page">
      <div className="travel-plan-container">
        <div className="travel-plan-header">
          <h1>✈️ 나의 여행 계획</h1>
          <div className="header-buttons">
            {isAuthenticated && (
              <button
                className="load-from-server-btn"
                onClick={handleLoadFromServer}
                disabled={isSyncing}
              >
                {isSyncing ? '불러오는 중...' : '☁️ 서버에서 불러오기'}
              </button>
            )}
            <button
              className="create-plan-btn"
              onClick={handleOpenCreateModal}
            >
              + 새 여행 계획
            </button>
          </div>
        </div>

        {travelPlans.length === 0 ? (
          <div className="empty-plans">
            <div className="empty-icon">🗺️</div>
            <h3>아직 여행 계획이 없습니다</h3>
            <p>새로운 여행 계획을 만들어 보세요!</p>
            <button
              className="create-first-plan-btn"
              onClick={handleOpenCreateModal}
            >
              첫 번째 여행 계획 만들기
            </button>
          </div>
        ) : (
          <div className="travel-plans-grid">
            {travelPlans.map(plan => (
              <div
                key={plan.id}
                className="travel-plan-card"
                onClick={() => handleViewPlan(plan.id)}
              >
                <div className="plan-card-header">
                  <div className="plan-title-section">
                    <h3 className="plan-title">{plan.title}</h3>
                    {/* 공유 배지 */}
                    {plan.is_owner === false && plan.permission && (
                      <span className={`share-badge share-badge-${plan.permission}`}>
                        {plan.permission === 'view' && '👁️ 읽기'}
                        {plan.permission === 'edit' && '✏️ 편집'}
                        {plan.permission === 'admin' && '⚙️ 관리자'}
                      </span>
                    )}
                    {plan.is_owner === true && (
                      <span className="share-badge share-badge-owner">
                        👤 내 여행
                      </span>
                    )}
                  </div>
                  <div className="plan-actions">
                    <button
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePlan(plan.id);
                      }}
                      title="삭제"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                <div className="plan-card-content">
                  <div className="plan-info">
                    <div className="info-item">
                      <span className="info-icon">📍</span>
                      <span className="info-text">{plan.destination}</span>
                    </div>

                    <div className="info-item">
                      <span className="info-icon">📅</span>
                      <span className="info-text">
                        {formatDate(plan.startDate)} ~ {formatDate(plan.endDate)}
                      </span>
                    </div>

                    <div className="info-item">
                      <span className="info-icon">⏰</span>
                      <span className="info-text">{plan.duration}일</span>
                    </div>

                    <div className="info-item">
                      <span className="info-icon">💰</span>
                      <span className="info-text">{formatBudget(plan.budget)}</span>
                    </div>
                  </div>

                  {plan.description && (
                    <div className="plan-description">
                      <p>{plan.description}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 새 여행 계획 생성 모달 */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-minimal">
              <button
                className="modal-close-btn"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>여행 제목</label>
                <input
                  type="text"
                  value={newPlan.title}
                  onChange={(e) => setNewPlan({...newPlan, title: e.target.value})}
                  placeholder="예: 제주도 힐링 여행 (미입력 시 목적지가 제목이 됩니다)"
                />
              </div>

              <div className="form-group">
                <label>목적지 *</label>
                <input
                  type="text"
                  value={newPlan.destination}
                  onChange={(e) => setNewPlan({...newPlan, destination: e.target.value})}
                  placeholder="예: 제주도"
                />
              </div>

              <div className="form-group">
                <label>여행 기간 *</label>
                <div className="date-range-container">
                  <input
                    type="date"
                    value={newPlan.startDate}
                    onChange={(e) => setNewPlan({...newPlan, startDate: e.target.value})}
                    className="date-range-input"
                    placeholder="시작 날짜"
                  />
                  <span className="date-range-separator">~</span>
                  <input
                    type="date"
                    value={newPlan.endDate}
                    onChange={(e) => setNewPlan({...newPlan, endDate: e.target.value})}
                    className="date-range-input"
                    placeholder="종료 날짜"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>예산</label>
                <input
                  type="text"
                  value={newPlan.budget}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setNewPlan({...newPlan, budget: value});
                  }}
                  placeholder="예: 500000"
                />
              </div>

              <div className="form-group">
                <label>설명</label>
                <textarea
                  value={newPlan.description}
                  onChange={(e) => setNewPlan({...newPlan, description: e.target.value})}
                  placeholder="여행에 대한 간단한 설명을 입력하세요..."
                  rows="3"
                />
              </div>

              {/* 참여자 관리 섹션 */}
              <div className="form-group">
                <div className="participants-header">
                  <label>참여자</label>
                  <button
                    type="button"
                    className="add-participant-btn"
                    onClick={() => setShowAddParticipant(true)}
                  >
                    + 참여자 추가
                  </button>
                </div>

                {/* 참여자 목록 */}
                {newPlan.participants.length > 0 && (
                  <div className="participants-list">
                    {newPlan.participants.map(participant => (
                      <div key={participant.id} className="participant-item">
                        <div className="participant-info">
                          <div className="participant-name">
                            <span className="participant-icon">{participant.icon || '👤'}</span>
                            {participant.name}
                          </div>
                          {participant.contact && (
                            <div className="participant-contact">{participant.contact}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="remove-participant-btn"
                          onClick={() => handleRemoveParticipant(participant.id)}
                          title="참여자 삭제"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {newPlan.participants.length === 0 && (
                  <div className="no-participants">
                    아직 추가된 참여자가 없습니다.
                  </div>
                )}

                {/* 참여자 추가 폼 */}
                {showAddParticipant && (
                  <div className="add-participant-form">
                    {/* 아이콘 선택 */}
                    <div className="icon-selector-container">
                      <label className="icon-selector-label">아이콘 선택</label>
                      <div className="icon-selector-grid">
                        {availableIcons.map((icon, index) => (
                          <button
                            key={index}
                            type="button"
                            className={`icon-option ${newParticipant.icon === icon ? 'selected' : ''}`}
                            onClick={() => setNewParticipant({...newParticipant, icon: icon})}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="participant-form-row">
                      <input
                        type="text"
                        placeholder="이름 *"
                        value={newParticipant.name}
                        onChange={(e) => setNewParticipant({...newParticipant, name: e.target.value})}
                        className="participant-name-input"
                      />
                      <input
                        type="text"
                        placeholder="이메일 또는 전화번호"
                        value={newParticipant.contact}
                        onChange={(e) => setNewParticipant({...newParticipant, contact: e.target.value})}
                        className="participant-contact-input"
                      />
                    </div>
                    <div className="participant-form-actions">
                      <button
                        type="button"
                        className="participant-save-btn"
                        onClick={handleAddParticipant}
                      >
                        추가
                      </button>
                      <button
                        type="button"
                        className="participant-cancel-btn"
                        onClick={() => {
                          setShowAddParticipant(false);
                          setNewParticipant({ name: '', contact: '' });
                        }}
                      >
                        취소
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowCreateModal(false)}
              >
                취소
              </button>
              <button
                className="create-btn"
                onClick={handleCreatePlan}
              >
                생성
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelPlanPage;